import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  Grid,
  LinearProgress,
  ListItemButton,
  Skeleton,
  Typography,
} from "@mui/material";
import Rating from "@mui/material/Rating";
import Ticket from "../../Component/common/Ticket";
import ScheduleCalendar from "../../Component/common/ScheduleCalendar";
import { useParams } from "react-router-dom";
import { getEventTimeInterval, getRestaurantEvent } from "../../api";
import Booking from "./Booking";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import MapContainer from "../../Component/common/MapContainer";
import { IoCalendarSharp } from "react-icons/io5";
import { GoClockFill } from "react-icons/go";
import { ImLocation } from "react-icons/im";

import { HiOutlineTicket } from "react-icons/hi2";
import { EVENT_SHIRO, useStep } from "../Home";
import {
  formatDate,
  formatScheduleTime,
  getEventSchedules,
  getOneMonthAgo,
  toBookingTime,
} from "../../utils";
import { useGetQuery } from "../../api/useQuery";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const style = {
  position: "fixed",
  top: "90%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 350,
  zIndex: 1000,
  boxShadow: 24,
  p: 1,
};

export const TicketPage = () => {
  const [loading, setLoading] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState([]); // support multiple ticket selection
  const { name, id } = useParams();
  const [value, setValue] = React.useState(0);
  const { step, setStep } = useStep(); // Use step context from Home component
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedScheduleKey, setSelectedScheduleKey] = useState(null);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const { data, isPending } = useGetQuery(
    ["event", id, name],
    `restaurantevent/${id}/name/${name}`,
  );

  const event_data = data?.result || null;
  const schedules = getEventSchedules(event_data);
  const hasSchedules = schedules.length > 0;
  const hasMultipleSchedules = schedules.length > 1;

  const [notTicketed, setNotTicketed] = useState(false);

  const [timeLoader, setTimeLoader] = useState(false);

  const [buffers, setBuffers] = useState(null);

  const getScheduleKey = (schedule, index) => {
    const date =
      schedule?.SpecificDate ||
      schedule?.specificDate ||
      schedule?.Date ||
      "";
    const start =
      schedule?.StartTime || schedule?.startTime || schedule?.Time || "";
    return `${date}|${start}|${index}`;
  };

  const applyScheduleSelection = (schedule, index) => {
    const key = getScheduleKey(schedule, index);
    setSelectedScheduleKey(key);
    setSelectedTime(
      toBookingTime(schedule?.StartTime || schedule?.startTime || schedule?.Time),
    );
    setNotTicketed(true);
  };

  const handleGetTimeInterval = async (id) => {
    setTimeLoader(true);
    await getEventTimeInterval(id)
      .then((res) => {
        setTimeLoader(false);
        // console.log(res);
        if (res?.data?.status) {
          setBuffers(res?.data?.result);
        } else {
          setBuffers(null);
        }
      })
      .catch((err) => {
        setTimeLoader(false);
        // console.log(err);
      });
  };

  useEffect(() => {
    if (!event_data) return;

    // Prefer Schedules when present; fall back to legacy buffer slots
    if (hasSchedules) {
      setBuffers(null);
      setTimeLoader(false);
      if (schedules.length === 1) {
        applyScheduleSelection(schedules[0], 0);
      }
      return;
    }

    handleGetTimeInterval(event_data?.EventId);
  }, [event_data, data]);

  const selectedSchedule =
    hasSchedules && selectedScheduleKey != null
      ? schedules.find(
          (schedule, index) =>
            getScheduleKey(schedule, index) === selectedScheduleKey,
        )
      : null;

  const displayDate =
    selectedSchedule?.SpecificDate ||
    selectedSchedule?.specificDate ||
    event_data?.EventDate;
  const displayTime = selectedSchedule
    ? formatScheduleTime(
        selectedSchedule?.StartTime ||
          selectedSchedule?.startTime ||
          selectedSchedule?.Time,
      )
    : event_data?.EventTime && event_data?.EventTime !== "N/A"
      ? event_data.EventTime
      : null;

  const requiresSchedulePick = hasMultipleSchedules;
  const scheduleReady = !requiresSchedulePick || selectedScheduleKey != null;
  const canShowTickets =
    event_data?.IsTicketed &&
    scheduleReady &&
    (selectedTime || !buffers || hasSchedules);

  const isShiroEvent = name === EVENT_SHIRO;
  const isShiroVal = name == "Valentines-at-Shiro-";
  const isLoungeSessions = name === "The-Lounge-Sessions";
  const maxTicketCount = isLoungeSessions ? 1 : null;
  const totalSelectedQuantity = selectedTickets.reduce(
    (sum, ticket) => sum + (ticket.Quantity || 0),
    0,
  );

  return (
    <>
      {step === 0 ? (
        <Box
          className="hide_scrollbar"
          sx={{ mt: 2, minHeight: "1000px", overflow: "scroll", p: 2 }}
        >
          <Grid container spacing={4}>
            <Grid item md={12} xs={12}>
              <Box>
                {isPending ? (
                  <Skeleton variant="square" sx={{ width: "30%" }} />
                ) : (
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "24px",
                      fontFamily: "optima",
                    }}
                  >
                    {event_data?.EventName}
                  </Typography>
                )}

                <Box sx={{ mt: 0 }}>
                  {isPending ? (
                    <Skeleton width="30%" variant="sqare" />
                  ) : (
                    <Rating
                      defaultValue={event_data?.ReviewDetails?.Rating}
                      precision={0.1}
                      sx={{ fontSize: "15px", color: "#DF8E14" }}
                      size="large"
                      readOnly
                    />
                  )}

                  <Box
                    sx={{ display: "flex", alignItems: "center", columnGap: 2 }}
                  >
                    {isPending ? (
                      <Skeleton width="40%" variant="square" />
                    ) : (
                      <></>
                    )}
                  </Box>

                  {isPending ? (
                    <Skeleton width="40%" variant="square" />
                  ) : (
                    <>
                      {displayDate &&
                        (!hasMultipleSchedules || selectedSchedule) && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            columnGap: 2,
                            mt: 2,
                          }}
                        >
                          <IoCalendarSharp />
                          <Typography
                            sx={{
                              fontFamily: "outfit",
                              fontWeight: 300,
                              fontSize: "13px",
                            }}
                          >
                            {formatDate(displayDate)}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      columnGap: 2,
                      mt: 2,
                    }}
                  >
                    {isPending ? (
                      <Skeleton width="40%" variant="square" />
                    ) : (
                      <>
                        {displayTime &&
                          (!hasMultipleSchedules || selectedSchedule) && (
                          <>
                            <GoClockFill />
                            <Typography
                              sx={{
                                fontFamily: "outfit",
                                fontWeight: 300,
                                fontSize: "13px",
                              }}
                            >
                              {displayTime}
                            </Typography>
                          </>
                        )}
                      </>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      columnGap: 1.5,
                      mt: 1,
                    }}
                  >
                    <ImLocation />
                    <Typography sx={{ fontWeight: 300, fontSize: "13px" }}>
                      {event_data?.EventAddress}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                      <Tabs
                        variant="fullWidth"
                        value={value}
                        onChange={handleChange}
                        aria-label="basic tabs example"
                      >
                        <Tab label="Bookings" {...a11yProps(0)} />
                        {/* <Tab label="Menu" {...a11yProps(1)} /> */}
                        {!isShiroEvent && (
                          <Tab label="Reviews" {...a11yProps(2)} />
                        )}

                        <Tab label="Details" {...a11yProps(3)} />
                      </Tabs>
                    </Box>
                    <CustomTabPanel value={value} index={0}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "16px",
                          fontFamily: "optima",
                        }}
                      >
                        Details
                      </Typography>
                      <Typography></Typography>
                      <ReactQuill
                        value={event_data?.EventDescription}
                        readOnly={true}
                        theme={null} // Disable theme
                        modules={{ toolbar: false }}
                        style={{ fontFamily: "outfit" }}
                        className="custom-quill-editor" // Apply custom CSS class
                      />
                    </CustomTabPanel>

                    <CustomTabPanel value={value} index={1}>
                      <Box sx={{ mt: 2 }}>
                        {!event_data?.ReviewDetails?.Reviews ||
                        event_data?.ReviewDetails?.Reviews?.length === 0 ? (
                          <>
                            <Typography sx={{}}>
                              No Reviews available
                            </Typography>
                          </>
                        ) : (
                          <>
                            {event_data?.ReviewDetails?.Reviews.map(
                              (review, i) => (
                                <Box key={i} sx={{ pb: 2 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      columnGap: 2,
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        bgcolor: "#013220",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {review?.ProfileImage ? (
                                        <img
                                          src={review?.ProfileImage}
                                          width={40}
                                        />
                                      ) : (
                                        review?.UserName?.substring(0, 2)
                                      )}
                                    </Avatar>
                                    <Box>
                                      <Typography sx={{ fontSize: "12px" }}>
                                        {review?.UserName || "Annymous"}
                                      </Typography>
                                      <Typography sx={{ fontSize: "10px" }}>
                                        {getOneMonthAgo(review?.DateAdded) ||
                                          "--"}{" "}
                                        . {review?.Time}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Rating
                                    readOnly
                                    value={review?.Rating}
                                    precision={0.5}
                                    sx={{
                                      fontSize: "12px",
                                      color: "#BC172F",
                                      my: 1,
                                    }}
                                  />
                                  <Typography sx={{ fontSize: "10px" }}>
                                    {review?.Comment}
                                  </Typography>
                                </Box>
                              ),
                            )}
                          </>
                        )}
                      </Box>
                    </CustomTabPanel>
                    <CustomTabPanel value={value} index={2}>
                      <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                        Details
                      </Typography>
                      <ReactQuill
                        value={event_data?.EventDescription}
                        readOnly={true}
                        theme={null} // Disable theme
                        modules={{ toolbar: false }}
                      />
                    </CustomTabPanel>
                  </Box>

                  <Box sx={{ mt: 1 }}>
                    {hasSchedules ? (
                      <Box>
                        {hasMultipleSchedules && (
                          <>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: "16px",
                                fontFamily: "optima",
                                mb: 1.5,
                                color: "#2B2B2B",
                              }}
                            >
                              Select date
                            </Typography>
                            <ScheduleCalendar
                              schedules={schedules}
                              selectedKey={selectedScheduleKey}
                              getScheduleKey={getScheduleKey}
                              onSelect={applyScheduleSelection}
                            />
                          </>
                        )}
                      </Box>
                    ) : timeLoader ? (
                      <>
                        <Box sx={{ height: "200px" }} align="center">
                          <LinearProgress />
                          <Typography
                            sx={{
                              fontSize: "12px",
                              textAlign: "center",
                              mt: 1,
                            }}
                          >
                            Loading available time for this event
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Grid container spacing={2}>
                        {buffers &&
                          buffers?.map((time, index) => {
                            return (
                              <Grid
                                item
                                lg={3}
                                md={3}
                                sm={3}
                                xs={3}
                                key={index}
                                sx={{}}
                              >
                                <ListItemButton
                                  onClick={() => {
                                    setSelectedTime(time);
                                    setNotTicketed(true);
                                  }}
                                  selected={selectedTime === time}
                                  sx={{
                                    borderRadius: "8px",
                                    bgcolor: "#bb182f",
                                    color: "#fff",
                                    transition: "0.1s all linear",
                                    display: "flex",
                                    height: "40px",

                                    "&:hover": {
                                      bgcolor: "#bb182fb7",
                                      border: "2px solid #d7d7d7",
                                    },
                                    "&.Mui-selected": {
                                      bgcolor: "#bb182f",
                                      border: "3px solid #000",
                                    },
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "10px",
                                      whiteSpace: "nowrap",
                                      textAlign: "center",
                                      width: "100%",
                                    }}
                                  >
                                    {" "}
                                    {time}
                                  </Typography>
                                </ListItemButton>
                              </Grid>
                            );
                          })}
                      </Grid>
                    )}
                  </Box>

                  {canShowTickets && (
                    <Box sx={{ mt: 4 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "16px",
                          fontFamily: "optima",
                          lineHeight: "12px",
                        }}
                      >
                        {isShiroVal ? "Minimum spend on F&B" : "Tickets"}
                      </Typography>
                      <p style={{ fontSize: "12px", color: "#333" }}>
                        You can select more than one ticket to purchase.
                      </p>

                      <Box sx={{ mt: 2 }}>
                        {isPending ? (
                          <>
                            <Skeleton
                              variant="square"
                              sx={{ height: "200px" }}
                            />
                            <Skeleton
                              variant="square"
                              sx={{ height: "200px", my: 2 }}
                            />
                            <Skeleton
                              variant="square"
                              sx={{ height: "200px" }}
                            />
                          </>
                        ) : (
                          <>
                            {event_data?.TicketDetails?.map((item, index) => {
                              const existing = selectedTickets.find(
                                (ticket) => ticket.TicketId === item?.TicketId,
                              );
                              const quantity = existing?.Quantity || 0;
                              const isSelected = quantity > 0;

                              const createSelectedTicket = () => ({
                                TicketId: item?.TicketId,
                                TicketType: item?.TicketType,
                                TicketName: item?.TicketName,
                                EventName: data?.EventName,
                                TicketCost: Number(item?.TicketCost) || 0,
                                TicketLeft: item?.TicketLeft,
                                Description: item?.TicketDetails,
                                Quantity: 1,
                              });

                              const handleToggleTicket = () => {
                                setSelectedTickets((prevSelected) => {
                                  const existingTicket = prevSelected.find(
                                    (ticket) =>
                                      ticket.TicketId === item?.TicketId,
                                  );

                                  if (
                                    existingTicket &&
                                    (existingTicket.Quantity || 0) > 0
                                  ) {
                                    // Unselect ticket entirely
                                    return prevSelected.filter(
                                      (ticket) =>
                                        ticket.TicketId !== item?.TicketId,
                                    );
                                  }

                                  const total = prevSelected.reduce(
                                    (sum, ticket) => sum + (ticket.Quantity || 0),
                                    0,
                                  );
                                  if (maxTicketCount && total >= maxTicketCount) {
                                    return prevSelected;
                                  }

                                  // Add ticket with initial quantity 1
                                  return [
                                    ...prevSelected,
                                    createSelectedTicket(),
                                  ];
                                });
                              };

                              const handleIncrease = () => {
                                setSelectedTickets((prevSelected) => {
                                  const idx = prevSelected.findIndex(
                                    (ticket) =>
                                      ticket.TicketId === item?.TicketId,
                                  );

                                  const total = prevSelected.reduce(
                                    (sum, ticket) => sum + (ticket.Quantity || 0),
                                    0,
                                  );
                                  if (maxTicketCount && total >= maxTicketCount) {
                                    return prevSelected;
                                  }

                                  // If not yet selected, add with quantity 1
                                  if (idx === -1) {
                                    return [
                                      ...prevSelected,
                                      createSelectedTicket(),
                                    ];
                                  }

                                  const current = prevSelected[idx];
                                  const currentQty = current.Quantity || 0;
                                  if (
                                    item?.TicketLeft !== undefined &&
                                    item?.TicketLeft !== null &&
                                    currentQty >= item.TicketLeft
                                  ) {
                                    return prevSelected;
                                  }

                                  const updated = [...prevSelected];
                                  updated[idx] = {
                                    ...current,
                                    Quantity: currentQty + 1,
                                  };
                                  return updated;
                                });
                              };

                              const handleDecrease = () => {
                                setSelectedTickets((prevSelected) => {
                                  const idx = prevSelected.findIndex(
                                    (ticket) =>
                                      ticket.TicketId === item?.TicketId,
                                  );
                                  if (idx === -1) return prevSelected;

                                  const current = prevSelected[idx];
                                  const currentQty = current.Quantity || 0;
                                  const newQty = currentQty - 1;

                                  if (newQty <= 0) {
                                    // Remove ticket when quantity drops to 0
                                    return prevSelected.filter(
                                      (ticket) =>
                                        ticket.TicketId !== item?.TicketId,
                                    );
                                  }

                                  const updated = [...prevSelected];
                                  updated[idx] = {
                                    ...current,
                                    Quantity: newQty,
                                  };
                                  return updated;
                                });
                              };

                              return (
                                <Ticket
                                  key={index}
                                  onClick={handleToggleTicket}
                                  data={item}
                                  type={item?.TicketType}
                                  price={item?.TicketCost}
                                  event={item?.TicketDetails}
                                  ticketLeft={item?.TicketLeft}
                                  name={event_data?.EventName}
                                  eventData={event_data}
                                  isSelected={isSelected}
                                  quantity={quantity}
                                  onIncrease={handleIncrease}
                                  onDecrease={handleDecrease}
                                  increaseDisabled={
                                    maxTicketCount
                                      ? totalSelectedQuantity >= maxTicketCount
                                      : false
                                  }
                                />
                              );
                            })}
                          </>
                        )}
                      </Box>
                    </Box>
                  )}

                  <Box style={style}>
                    <Box
                      sx={{
                        bgcolor: "#fff",
                        borderRadius: "8px",
                        p: 0.4,
                        boxShadow: 24,
                      }}
                    >
                      {event_data?.IsTicketed ? (
                        <Button
                          disabled={
                            selectedTickets.length === 0 || !scheduleReady
                          }
                          fullWidth
                          variant="contained"
                          onClick={() => {
                            // Proceed to booking step only after ticket selection
                            setStep(2);
                          }}
                          sx={{ height: "45px", borderRadius: "8px" }}
                        >
                          Continue to Booking
                        </Button>
                      ) : (
                        <Button
                          disabled={
                            (buffers && !selectedTime) ||
                            (requiresSchedulePick && !selectedTime)
                          }
                          fullWidth
                          variant="contained"
                          onClick={() => {
                            // Non-ticketed events proceed directly without ticket selection
                            setStep(2);
                          }}
                          sx={{ height: "45px", borderRadius: "8px" }}
                        >
                          Book Event
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item md={12} sm={12} xs={12} sx={{ overflow: "hidden" }}>
              <MapContainer
                variant={"map"}
                restaurantAddress={event_data?.EventAddress}
              />

              <Box
                sx={{
                  p: 3,
                  boxSizing: "border-box",
                  border: "1px solid #d7d7d7",
                }}
              >
                <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
                  Locate {event_data?.EventName}
                </Typography>

                <Typography sx={{ mt: 0.5, fontSize: "12px" }}>
                  {event_data?.EventAddress}
                </Typography>

                <Box sx={{ mt: 3 }}>
                  <a href={`tel:${event_data?.PhoneNumaber}`}>
                    <Typography color={"primary"}>
                      {event_data?.PhoneNumaber}
                    </Typography>
                  </a>
                  <Box sx={{ mt: 2 }}>
                    <MapContainer
                      variant="button"
                      restaurantAddress={event_data?.EventAddress}
                    />
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <a href={`mailto:${event_data?.EmailAddress}`}>
                      <Typography color="primary">
                        {event_data?.EmailAddress}
                      </Typography>
                    </a>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Booking
          tax={event_data?.TaxPercantage || 0}
          event={event_data?.EventId}
          selectedTickets={selectedTickets}
          setStep={() => setStep(0)}
          time={selectedTime}
          date={
            selectedSchedule?.SpecificDate ||
            selectedSchedule?.specificDate ||
            event_data?.EventDate ||
            null
          }
          isTicketed={event_data?.IsTicketed}
          // ticket_id={selectedTickets}
        />
      )}
    </>
  );
};
